import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import FakeLoading from '../ui/FakeLoading';
import { API_BASE_URL } from '../../config';
import {
  GI_AI_MAX_SELECTED_CAMPAIGNS,
  GI_AI_MAX_SELECTED_QUESTIONS,
  getGiAiContextReductionPreview
} from '../../utils/giAiCustomPromptGuardrails';

const dedupeSurveyQuestionPairs = (pairs) => {
  const map = new Map();
  pairs.forEach((p) => {
    if (!p?.surveyId || !p?.questionId) return;
    const key = `${p.surveyId}-${p.questionId}`;
    map.set(key, { surveyId: p.surveyId, questionId: p.questionId });
  });
  return Array.from(map.values());
};

const GIAICustomPromptModal = ({
  isOpen,
  onClose,
  groupedQuestions,
  selectedCampaigns,
  fetchWithAuth,
  clientId,
  startDate,
  endDate
}) => {
  const [prompt, setPrompt] = useState('');
  const [selectedQuestionTexts, setSelectedQuestionTexts] = useState(() => new Set());
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [resultError, setResultError] = useState(null);
  const [resultMetadata, setResultMetadata] = useState(null);
  const [selectionWarning, setSelectionWarning] = useState('');
  const abortControllerRef = useRef(null);

  const selectableQuestions = useMemo(() => groupedQuestions || [], [groupedQuestions]);

  const selectedGroups = useMemo(() => {
    if (!selectableQuestions.length) return [];
    return selectableQuestions.filter(q => selectedQuestionTexts.has(q.questionText));
  }, [selectableQuestions, selectedQuestionTexts]);

  const selectedTotalAnswers = useMemo(() => {
    return selectedGroups.reduce((sum, q) => sum + (q.totalResponses || 0), 0);
  }, [selectedGroups]);

  const selectedCampaignIds = useMemo(() => {
    return Array.from(new Set((selectedCampaigns || []).map((campaign) => campaign.value).filter(Boolean)));
  }, [selectedCampaigns]);

  const contextReductionPreview = useMemo(() => {
    return getGiAiContextReductionPreview(selectedTotalAnswers);
  }, [selectedTotalAnswers]);

  const hasContextLossPreview = contextReductionPreview.rawAnswersCount > 0
    && contextReductionPreview.contextLossRate > 0;

  const handleSelectNone = () => {
    setSelectedQuestionTexts(new Set());
    setSelectionWarning('');
  };

  const toggleQuestion = (questionText) => {
    const isSelected = selectedQuestionTexts.has(questionText);

    if (!isSelected && selectedQuestionTexts.size >= GI_AI_MAX_SELECTED_QUESTIONS) {
      setSelectionWarning(`You can select up to ${GI_AI_MAX_SELECTED_QUESTIONS} questions.`);
      return;
    }

    setSelectionWarning('');
    setSelectedQuestionTexts((prev) => {
      const next = new Set(prev);
      if (next.has(questionText)) {
        next.delete(questionText);
      } else {
        next.add(questionText);
      }
      return next;
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    setSelectedQuestionTexts(new Set());
    setPrompt('');
    setIsRunning(false);
    setResult(null);
    setResultError(null);
    setResultMetadata(null);
    setSelectionWarning('');
    if (abortControllerRef.current) abortControllerRef.current.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    setSelectedQuestionTexts((prev) => {
      const availableQuestions = new Set(selectableQuestions.map((question) => question.questionText));
      const next = new Set([...prev].filter((questionText) => availableQuestions.has(questionText)));
      return next;
    });
  }, [isOpen, selectableQuestions]);

  const handleRun = async () => {
    const trimmedPrompt = (prompt || '').trim();
    if (!trimmedPrompt) {
      setResultError('Please enter a prompt.');
      return;
    }

    if (!startDate || !endDate) {
      setResultError('Start and end dates are required.');
      return;
    }

    if (!clientId) {
      setResultError('Client is required.');
      return;
    }

    if (selectedGroups.length === 0) {
      setResultError('Please select at least one question.');
      return;
    }

    if (selectedCampaignIds.length === 0) {
      setResultError('Please select at least one campaign.');
      return;
    }

    const rawPairs = selectedGroups.flatMap(q =>
      (q.surveyDetails || []).map(d => ({ surveyId: d.surveyId, questionId: d.questionId }))
    );
    const surveyQuestionPairs = dedupeSurveyQuestionPairs(rawPairs);

    if (surveyQuestionPairs.length === 0) {
      setResultError('No valid survey/question pairs found for the selected questions.');
      return;
    }

    setIsRunning(true);
    setResult(''); // allow progressive rendering while streaming
    setResultError(null);
    setResultMetadata(null);

    try {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const response = await fetchWithAuth(`${API_BASE_URL}/api/ia/generate-longtext-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/x-ndjson'
        },
        signal: controller.signal,
        returnRawResponse: true,
        body: JSON.stringify({
          clientId,
          surveyQuestionPairs,
          selectedCampaignIds,
          selectedQuestionCount: selectedGroups.length,
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0],
          prompt: trimmedPrompt,
          force: false,
          stream: true
        })
      });

      const contentType = (response.headers.get('content-type') || '').toLowerCase();
      const isNdjson = contentType.includes('application/x-ndjson');

      // Fallback (non-stream)
      if (!isNdjson || !response.body) {
        if (!response.ok) {
          let errorData = null;
          try {
            errorData = await response.json();
          } catch (e) {
            // ignore
          }
          throw new Error(errorData?.error || errorData?.message || `Error ${response.status}: ${response.statusText}`);
        }
        const data = await response.json();
        setResult(data.result);
        setResultMetadata({
          cached: data.cached,
          ownerName: data.owner_name,
          answersCount: data.answers_count,
          timestamp: data.timestamp,
          contextReduction: data.context_reduction || null
        });
        return;
      }

      // Stream NDJSON
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';
      let lastUiFlush = 0;

      const flushUi = () => {
        setResult(accumulated);
        lastUiFlush = Date.now();
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed) continue;

          let evt = null;
          try {
            evt = JSON.parse(trimmed);
          } catch (e) {
            continue;
          }

          if (evt.type === 'meta') {
            setResultMetadata({
              cached: evt.cached,
              ownerName: evt.owner_name,
              answersCount: evt.answers_count,
              timestamp: evt.timestamp,
              contextReduction: evt.context_reduction || null
            });
            continue;
          }

          if (evt.type === 'delta') {
            accumulated += evt.delta || '';
            const now = Date.now();
            if (now - lastUiFlush > 120) flushUi();
            continue;
          }

          if (evt.type === 'error') {
            setResultError(evt.error || 'Failed to run prompt.');
            continue;
          }

          if (evt.type === 'done') {
            if (typeof evt.result === 'string') accumulated = evt.result;
            flushUi();
            if (evt.timestamp || evt.owner_name || evt.answers_count !== undefined || evt.cached !== undefined) {
              setResultMetadata({
                cached: evt.cached,
                ownerName: evt.owner_name,
                answersCount: evt.answers_count,
                timestamp: evt.timestamp,
                contextReduction: evt.context_reduction || null
              });
            }
          }
        }
      }

      // Final flush (in case last chunk didn't pass throttle)
      setResult(accumulated);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('Error running custom prompt:', error);
      setResultError(error.message || 'Failed to run prompt.');
    } finally {
      setIsRunning(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>

      <div className="flex min-h-full items-center justify-center p-4 sm:p-6 lg:p-8">
        <div
          className="relative bg-white dark:bg-gray-900 rounded-lg shadow-xl w-[96vw] max-w-[1600px] h-[92vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4 rounded-t-lg">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Custom prompt on open questions
                </h1>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                  <span>
                    Selected: <span className="font-medium">{selectedGroups.length}/{GI_AI_MAX_SELECTED_QUESTIONS}</span> question{selectedGroups.length > 1 ? 's' : ''}
                  </span>
                  <span className="text-gray-400">•</span>
                  <span>
                    Campaigns: <span className="font-medium">{selectedCampaignIds.length}/{GI_AI_MAX_SELECTED_CAMPAIGNS}</span>
                  </span>
                  <span className="text-gray-400">•</span>
                  <span>
                    Answers: <span className="font-medium">{selectedTotalAnswers}</span>
                  </span>
                  {hasContextLossPreview && (
                    <>
                      <span className="text-gray-400">•</span>
                      <span className="font-medium text-orange-600 dark:text-orange-300">
                        Sampling: {Math.round(contextReductionPreview.retentionRate * 100)}% kept ({contextReductionPreview.keptAnswers}/{contextReductionPreview.rawAnswersCount})
                      </span>
                    </>
                  )}
                  {selectionWarning && (
                    <>
                      <span className="text-gray-400">•</span>
                      <span className="font-medium text-orange-600 dark:text-orange-300">
                        {selectionWarning}
                      </span>
                    </>
                  )}
                  {resultMetadata?.timestamp && (
                    <>
                      <span className="text-gray-400">•</span>
                      <span>
                        Last run: <span className="font-medium">{new Date(resultMetadata.timestamp).toLocaleString('fr-FR')}</span>
                      </span>
                    </>
                  )}
                  {resultMetadata?.cached !== undefined && (
                    <>
                      <span className="text-gray-400">•</span>
                      <span>
                        Cached: <span className="font-medium">{resultMetadata.cached ? 'yes' : 'no'}</span>
                      </span>
                    </>
                  )}
                </div>
              </div>

              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                aria-label="Close modal"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-3">
            {/* Left sidebar */}
            <div className="lg:col-span-1 min-h-0 overflow-hidden border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
              <div className="h-full min-h-0 overflow-hidden flex flex-col p-5 gap-5">
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex-shrink-0">
                  <div className="flex items-center justify-between gap-4 mb-3">
                    <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                      Prompt
                    </h2>
                    <button
                      onClick={handleRun}
                      disabled={isRunning || selectedGroups.length === 0}
                      className={`px-3 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium ${
                        isRunning || selectedGroups.length === 0
                          ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {isRunning ? (
                        <>
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Running...
                        </>
                      ) : (
                        <>
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          Run
                        </>
                      )}
                    </button>
                  </div>

                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    className="w-full min-h-[180px] resize-y rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Write your question / prompt here. Example: Summarize the top 5 themes, include representative quotes, and suggest concrete actions."
                  />
                </div>

                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg flex-1 min-h-0 overflow-hidden flex flex-col">
                  <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
                    <div>
                      <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                        Open questions
                      </h2>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Only questions with at least 1 answer can be selected. Maximum: {GI_AI_MAX_SELECTED_QUESTIONS}.
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={handleSelectNone}
                        className="px-2.5 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      >
                        None
                      </button>
                    </div>
                  </div>

                  <div className="flex-1 min-h-0 overflow-y-auto">
                    {selectableQuestions.length === 0 ? (
                      <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                        No Open questions available for the current selection.
                      </div>
                    ) : (
                      <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                        {selectableQuestions.map((q) => {
                          const disabled = (q.totalResponses || 0) <= 0;
                          const checked = selectedQuestionTexts.has(q.questionText);
                          return (
                            <li key={q.questionText} className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-900/30">
                              <label className={`flex items-start gap-3 ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                                <input
                                  type="checkbox"
                                  className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  checked={checked}
                                  disabled={disabled}
                                  onChange={() => toggleQuestion(q.questionText)}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-2">
                                    {q.questionText}
                                  </div>
                                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                                    <span>{q.totalResponses} answer{q.totalResponses > 1 ? 's' : ''}</span>
                                    <span className="text-gray-400">•</span>
                                    <span>{q.surveys} form{q.surveys > 1 ? 's' : ''}</span>
                                  </div>
                                </div>
                              </label>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right pane */}
            <div className="lg:col-span-2 min-h-0 overflow-hidden bg-gray-50 dark:bg-gray-900">
              <div className="h-full min-h-0 overflow-hidden p-6 flex flex-col gap-6">
                {resultError && (
                  <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-orange-700 dark:text-orange-300 font-medium">Oops!</span>
                    </div>
                    <p className="text-orange-600 dark:text-orange-400 mt-1">{resultError}</p>
                  </div>
                )}

                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 overflow-hidden flex-1 min-h-0 flex flex-col">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex items-center gap-3">
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Result
                      </h2>
                      {isRunning && (
                        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Streaming...
                        </div>
                      )}
                    </div>
                    {resultMetadata?.ownerName && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Generated by {resultMetadata.ownerName}
                        {resultMetadata.answersCount ? ` • ${resultMetadata.answersCount} answers` : ''}
                      </div>
                    )}
                  </div>

                  {resultMetadata?.contextReduction?.contextLossRate > 0 ? (
                    <div className="mb-4 rounded-md border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-700 dark:border-orange-900 dark:bg-orange-950/40 dark:text-orange-200">
                      Context reduced before prompt execution: {resultMetadata.contextReduction.modelWeightedResponseCount} / {resultMetadata.contextReduction.rawResponseCount} answers kept ({Math.round((resultMetadata.contextReduction.retentionRate || 0) * 100)}%).
                    </div>
                  ) : null}

                  <div className="prose prose-lg dark:prose-invert max-w-none text-left overflow-y-auto flex-1 min-h-0 pr-2">
                    {typeof result === 'string' && result.length > 0 ? (
                      <ReactMarkdown
                        components={{
                          h1: ({children}) => <h1 className="text-left text-2xl font-bold mb-4 text-gray-900 dark:text-white">{children}</h1>,
                          h2: ({children}) => <h2 className="text-left text-xl font-semibold mb-3 text-gray-800 dark:text-gray-100 mt-6">{children}</h2>,
                          h3: ({children}) => <h3 className="text-left text-lg font-medium mb-2 text-gray-700 dark:text-gray-200 mt-4">{children}</h3>,
                          p: ({children}) => <p className="text-left mb-3 text-gray-700 dark:text-gray-300 leading-relaxed">{children}</p>,
                          ul: ({children}) => <ul className="text-left list-disc pl-6 mb-4 space-y-1">{children}</ul>,
                          ol: ({children}) => <ol className="text-left list-decimal pl-6 mb-4 space-y-1">{children}</ol>,
                          li: ({children}) => <li className="text-left text-gray-700 dark:text-gray-300">{children}</li>,
                          strong: ({children}) => <strong className="text-left font-semibold text-gray-800 dark:text-gray-200">{children}</strong>,
                          em: ({children}) => <em className="text-left italic text-gray-600 dark:text-gray-400">{children}</em>,
                          hr: () => <hr className="my-6 border-gray-200 dark:border-gray-700" />
                        }}
                      >
                        {result}
                      </ReactMarkdown>
                    ) : isRunning ? (
                      <FakeLoading
                        maxDuration={selectedTotalAnswers < 100 ? 8000 : selectedTotalAnswers < 500 ? 13000 : 20000}
                        className="py-6"
                      />
                    ) : (
                      <div className="py-10 text-center text-gray-500 dark:text-gray-400">
                        Write your prompt, select questions on the left, then click “Run”.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GIAICustomPromptModal;
